import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { Brain, Workflow, BarChart3, Rocket } from "lucide-react";
import { useSpring, animated, config } from "@react-spring/web";
import { useInView } from "react-intersection-observer";

interface ProcessStepProps {
  icon: React.ElementType;
  titleEn: string;
  titleFr: string;
  descEn: string;
  descFr: string;
  index: number;
  theme: string;
  language: string;
}

// Single Step Card
const ProcessStep: React.FC<ProcessStepProps> = ({
  icon: Icon,
  titleEn,
  titleFr,
  descEn,
  descFr,
  index,
  theme,
  language,
}) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const style = useSpring({
    from: { opacity: 0, transform: "translateY(50px) scale(0.9)" },
    to: {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0) scale(1)" : "translateY(50px) scale(0.9)",
    },
    config: { tension: 120, friction: 14 },
    delay: index * 200,
  });

  const isDark = theme === "dark";

  return (
    <animated.div ref={ref} style={style} className={`
      p-8
      rounded-2xl
      border-2
      h-full
      transition-all
      duration-300
      hover:-translate-y-1
      ${isDark 
        ? "bg-black/80 border-green-500/30 hover:border-green-400" 
        : "bg-white/80 border-green-900/30 hover:border-green-800"
      }
    `}>
      <div className="space-y-8">
        {/* Icon Container */}
        <div className={`
          w-24 h-24
          rounded-2xl
          flex items-center justify-center
          mb-12
          ${isDark 
            ? "bg-green-500/10 border border-green-500/30" 
            : "bg-green-900/10 border border-green-900/30"
          }
        `}>
          <Icon 
            className={`w-12 h-12 ${
              isDark ? "text-green-400" : "text-green-900"
            }`} 
          />
        </div>

        {/* Content */}
        <div className="space-y-8">
          <h3 className={`text-2xl font-bold ${
            isDark ? "text-green-400" : "text-green-900"
          }`}>
            {language === "en" ? titleEn : titleFr}
          </h3>
          <p className={`text-xl pr-6 ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}>
            {language === "en" ? descEn : descFr}
          </p>
        </div>
      </div>
    </animated.div>
  );
};

// The Wave Path Anim
const AnimatedPath = ({ theme }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Simple strokeDashoffset animation from 100 → 0
  const { strokeDashoffset } = useSpring({
    from: { strokeDashoffset: 100 },
    to: { strokeDashoffset: inView ? 0 : 100 },
    config: config.slow,
  });

  return (
    <svg
      ref={ref}
      className="absolute top-1/2 left-0 w-full h-24 -translate-y-1/2"
      viewBox="0 0 100 20"
    >
      <animated.path
        d="M 0 10 Q 25 15 50 10 T 100 10"
        fill="none"
        stroke={theme === "dark" ? "#059669" : "#059669"}
        strokeWidth="2"
        strokeDasharray="100"
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
      />
    </svg>
  );
};

const Approach = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const features = [
    {
      icon: Workflow,
      titleEn: "Process Optimization",
      titleFr: "Optimisation des Processus",
      descEn:
        "Streamline workflows and eliminate bottlenecks through data-driven process improvements.",
      descFr:
        "Optimisez les flux de travail et éliminez les goulots d'étranglement grâce à des améliorations basées sur les données.",
    },
    {
      icon: BarChart3,
      titleEn: "Performance Metrics",
      titleFr: "Métriques de Performance",
      descEn:
        "Track and measure key performance indicators to ensure continuous improvement.",
      descFr:
        "Suivez et mesurez les indicateurs de performance clés pour assurer une amélioration continue.",
    },
    {
      icon: Rocket,
      titleEn: "Digital Transformation",
      titleFr: "Transformation Digitale",
      descEn:
        "Implement cutting-edge technologies to revolutionize your business operations.",
      descFr:
        "Implémentez des technologies de pointe pour révolutionner vos opérations commerciales.",
    },
  ];

  const isDark = theme === "dark";

  return (
    <section 
      id="approach"
      className="relative py-32 px-8 overflow-hidden">
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-20">
          <h2 className={`
            text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-6
            animate-gradient-x
          `}
          style={{ lineHeight: '1.2', paddingBottom: '10px' }}>
            {language === "en" ? "Our Approach" : "Notre Approche"}
          </h2>
          <div className={`w-24 h-1 mx-auto rounded-full mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />
        </div>

        {/* Large Container Card */}
        <div className={`
          p-12
          rounded-3xl
          border-2
          ${isDark 
            ? "bg-black/50 border-green-500/30" 
            : "bg-white/50 border-green-900/30"
          }
        `}>
          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <ProcessStep
                key={index}
                icon={feature.icon}
                titleEn={feature.titleEn}
                titleFr={feature.titleFr}
                descEn={feature.descEn}
                descFr={feature.descFr}
                index={index}
                theme={theme}
                language={language}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default Approach;
